import type { PersonaCopyContext, PersonaCopyEntry, PersonaCopyKind } from "./personaCopyKinds";
import { PERSONA_COPY_POOLS } from "./personaCopyPools";
import type { ResultsPersonaId } from "./personaIds";

/** FNV-1a 32-bit — deterministic, sync (Workers-safe). */
export function personaCopyHash(input: string): number {
	let h = 2166136261;
	for (let i = 0; i < input.length; i++) {
		h ^= input.charCodeAt(i);
		h = Math.imul(h, 16777619);
	}
	return h >>> 0;
}

export function interpolatePersonaTemplate(template: string, ctx: PersonaCopyContext): string {
	return template
		.replace(/\{q\}/g, ctx.question ?? "")
		.replace(/\{h\}/g, String(Math.max(1, ctx.hoursLeft ?? 1)));
}

function resolveEntry(entry: PersonaCopyEntry, ctx: PersonaCopyContext): string {
	if (typeof entry === "function") return entry(ctx);
	return interpolatePersonaTemplate(entry, ctx);
}

/**
 * Pick one variant from a persona × kind pool.
 * Seed should be stable per poll (e.g. roomId) so the same poll keeps consistent copy.
 */
export function pickPersonaLine(
	personaId: ResultsPersonaId,
	kind: PersonaCopyKind,
	seed: string,
	ctx: PersonaCopyContext = {},
): string {
	const pool = PERSONA_COPY_POOLS[personaId]?.[kind] ?? PERSONA_COPY_POOLS.neutral[kind];
	if (!pool?.length) {
		throw new Error(`Missing persona copy pool: ${personaId}.${kind}`);
	}
	const idx = personaCopyHash(`${seed}:${personaId}:${kind}`) % pool.length;
	return resolveEntry(pool[idx]!, ctx);
}

/** All variants for a kind (tests, introspection). */
export function listPersonaCopyVariants(
	personaId: ResultsPersonaId,
	kind: PersonaCopyKind,
	ctx: PersonaCopyContext = {},
): string[] {
	const pool = PERSONA_COPY_POOLS[personaId]?.[kind] ?? PERSONA_COPY_POOLS.neutral[kind];
	return pool.map((entry) => resolveEntry(entry, ctx));
}
